import Axios from 'axios';
import { getFromLocalStorageWithExpiry, setLocalStorageWithExpiry } from '../localstorage';
import axios from 'axios';

/* eslint-disable import/no-anonymous-default-export */
const client_id = process.env.REACT_APP_SPOTIFY_CLIENT_ID as string;
// Use environment variable if set, otherwise fall back to current origin + /callback
const redirect_uri = process.env.REACT_APP_SPOTIFY_REDIRECT_URL || 
  (typeof window !== 'undefined' ? `${window.location.origin}/callback` : '');

const authUrl = new URL('https://accounts.spotify.com/authorize');

// Cache to prevent duplicate concurrent token resolutions (e.g., StrictMode double-invoke)
let getTokenInFlight: Promise<[string | null, boolean]> | null = null;
let cachedTokenResult: [string | null, boolean] | null = null;
// Prevent multiple simultaneous refreshes or redirects.
let refreshInFlight: Promise<string | null> | null = null;
let authRedirectInFlight = false;
let reauthRequested = false;
let refreshTransientError = false;
export const hadTransientRefreshError = () => refreshTransientError;

const REFRESH_TOKEN_TTL_MS = 365 * 24 * 60 * 60 * 1000;

const SCOPES = [
  'ugc-image-upload',
  'streaming',

  'user-read-playback-state',
  'user-modify-playback-state',
  'user-read-currently-playing',

  'playlist-read-private',
  'playlist-modify-public',
  'playlist-modify-private',
  'playlist-read-collaborative',

  'user-follow-modify',
  'user-follow-read',

  'user-read-playback-position',
  'user-top-read',
  'user-read-recently-played',

  'user-library-read',
  'user-library-modify',
] as const;

const AUTH_TRACE = '[AuthTrace] login';

const maskToken = (token?: string | null) => {
  if (!token) return 'null';
  if (token.length <= 8) return token;
  return `${token.slice(0, 4)}...${token.slice(-4)}`;
};

const REQUIRED_SCOPES = [
  'streaming',
  'user-read-playback-state',
  'user-modify-playback-state',
  'user-read-currently-playing',
] as const;

const sha256 = async (plain: string) => {
  // Check if Web Crypto API is available
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return window.crypto.subtle.digest('SHA-256', data);
  }
  
  // Fallback: Use a simple hash function for development
  // Note: This is not cryptographically secure, only for development
  console.warn('Web Crypto API not available, using fallback hash function');
  
  // Simple hash function (not cryptographically secure)
  let hash = 0;
  for (let i = 0; i < plain.length; i++) {
    const char = plain.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Convert to ArrayBuffer-like structure
  const buffer = new ArrayBuffer(32);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < 32; i++) {
    view[i] = (hash >> (i * 8)) & 0xFF;
  }
  
  return buffer;
};

const base64encode = (input: ArrayBuffer) => {
  // @ts-ignore
  return btoa(String.fromCharCode(...new Uint8Array(input)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
};

const generateRandomString = (length: number) => {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return values.reduce((acc, x) => acc + possible[x % possible.length], '');
};

const shouldThrottleRedirect = () => {
  try {
    const last = localStorage.getItem('cmf_last_auth_redirect_at');
    if (!last) return false;
    const parsed = Number(last);
    if (!Number.isFinite(parsed)) return false;
    const now = Date.now();
    // Throttle reauth redirects within 10 seconds.
    return now - parsed < 10_000;
  } catch {
    return false;
  }
};

const hasRequiredScopes = (scopeString: string | null): boolean => {
  if (!scopeString) return false;
  const scopes = scopeString.split(' ').filter(Boolean);
  return REQUIRED_SCOPES.every((required) => scopes.includes(required));
};

const logInWithSpotify = async (anonymous?: boolean): Promise<boolean> => {
  if (authRedirectInFlight || shouldThrottleRedirect()) {
    console.info(`${AUTH_TRACE} logInWithSpotify ignored; redirect already in flight/throttled`, { anonymous });
    return false;
  }
  authRedirectInFlight = true;
  console.info(`${AUTH_TRACE} logInWithSpotify`, { anonymous });
  let codeVerifier = localStorage.getItem('code_verifier');

  if (!codeVerifier) {
    codeVerifier = generateRandomString(64);
    localStorage.setItem('code_verifier', codeVerifier);
  }

  const hashed = await sha256(codeVerifier);
  const codeChallenge = base64encode(hashed);

  if (anonymous) {
    authUrl.search = new URLSearchParams({
      client_id,
      scope: '',
      redirect_uri,
      response_type: 'token',
    }).toString();
  } else {
    const requestedScopes = SCOPES.join(' ');
    console.info('[Auth] Requesting scopes', requestedScopes);
    try {
      localStorage.setItem('cmf_requested_scopes', requestedScopes);
    } catch {}
    authUrl.search = new URLSearchParams({
      client_id,
      redirect_uri,
      response_type: 'code',
      scope: requestedScopes,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
    }).toString();
  }
  try {
    localStorage.setItem('cmf_last_auth_redirect_at', String(Date.now()));
  } catch {}
  window.location.href = authUrl.toString();
  return true;
};

const requestToken = async (code: string) => {
  const code_verifier = localStorage.getItem('code_verifier') as string;

  if (!code_verifier) {
    throw new Error('Missing code_verifier. Please restart Spotify authentication.');
  }

  if (!redirect_uri) {
    throw new Error('Missing redirect_uri. Please set REACT_APP_SPOTIFY_REDIRECT_URL environment variable.');
  }

  const body = new URLSearchParams({
    code,
    client_id,
    redirect_uri,
    code_verifier,
    grant_type: 'authorization_code',
  });

  try {
    const { data: response } = await Axios.post<{
      access_token: string;
      token_type: string;
      expires_in: number;
      refresh_token: string;
      scope?: string;
    }>('https://accounts.spotify.com/api/token', body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    console.info('[Auth] Token exchange scopes', response.scope);
    try {
      localStorage.setItem('cmf_last_token_scope', response.scope ?? '');
    } catch {}
    reauthRequested = false;

    if (response.access_token) {
      const ttlMs = response.expires_in * 1000; // expires_in is in seconds, convert to milliseconds
      setLocalStorageWithExpiry('access_token', response.access_token, ttlMs);
      setLocalStorageWithExpiry('spotify_access_token', response.access_token, ttlMs);
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + response.access_token;
      // Persist refresh token with a long TTL so JSON-parsing storage reads do not throw
      setLocalStorageWithExpiry('refresh_token', response.refresh_token, REFRESH_TOKEN_TTL_MS);
      setLocalStorageWithExpiry('spotify_refresh_token', response.refresh_token, REFRESH_TOKEN_TTL_MS);
    }

    return response.access_token;
  } catch (error: any) {
    console.error('[requestToken] Token exchange failed', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      redirect_uri,
      hasCodeVerifier: !!code_verifier,
      codeLength: code?.length,
    });
    throw error;
  }
};

// Export function to clear cache when token is rejected (e.g., 401 error)
export const clearTokenCache = () => {
  if (cachedTokenResult) {
    console.info('[AuthRefresh] clearTokenCache - invalidating cached token');
    cachedTokenResult = null;
  }
};

const getToken = async (): Promise<[string | null, boolean]> => {
  if (cachedTokenResult) {
    // Return cached result without checking localStorage
    // Cache will be invalidated if token is rejected (401 error)
    console.info(`${AUTH_TRACE} getToken cached result`, {
      token: maskToken(cachedTokenResult[0]),
      loaded: cachedTokenResult[1],
    });
    return cachedTokenResult;
  }
  if (getTokenInFlight) {
    console.info(`${AUTH_TRACE} getToken returning in-flight promise`);
    return getTokenInFlight;
  }

  let shouldCacheTokenResult = true;
  getTokenInFlight = (async (): Promise<[string | null, boolean]> => {
    console.log('[getToken] Called');
    // Check for callback code FIRST - even if a token exists, process the callback
    // (This handles cases where user authenticates with a new account while old token exists)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code') as string;
    console.log('[getToken] Code check', { hasCode: !!code, codeLength: code?.length });
    if (code) {
      try {
        const storedScopes = localStorage.getItem('cmf_requested_scopes');
        console.info('[Auth] Requested scopes (from storage)', storedScopes);
      } catch {}
      console.log('[getToken] Processing code, calling requestToken');
      const token = await requestToken(code);
      console.log('[getToken] requestToken succeeded', { hasToken: !!token });
      const result: [string | null, boolean] = [token, true];
      cachedTokenResult = result;
      return result;
    }

    // No callback code - check for existing token
    const token = getFromLocalStorageWithExpiry('access_token');
    console.info('[AuthRefresh] getToken checking for access_token', { hasToken: !!token });
    const storedScopes = (() => {
      try {
        return localStorage.getItem('cmf_last_token_scope');
      } catch {
        return null;
      }
    })();
    if (token) {
      const scopesOk = hasRequiredScopes(storedScopes);
      console.info(`${AUTH_TRACE} getToken using stored access_token`, {
        token: maskToken(token),
        scopesOk,
        scopes: storedScopes,
      });
      if (scopesOk) {
        const result: [string | null, boolean] = [token, true];
        cachedTokenResult = result;
        reauthRequested = false;
        return result;
      }
      console.warn(`${AUTH_TRACE} stored token missing required scopes, clearing`, {
        token: maskToken(token),
        scopes: storedScopes,
      });
      try {
        localStorage.removeItem('access_token');
        localStorage.removeItem('spotify_access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('spotify_refresh_token');
      } catch {}
    }

    // Try to mint a new access token from refresh token before falling back to public/implicit flows
    try {
      console.info('[AuthRefresh] getToken attempting refresh from stored token');
      const refreshed = await getRefreshToken();
      if (refreshed) {
        console.info(`${AUTH_TRACE} getToken obtained refreshed token`);
        const result: [string | null, boolean] = [refreshed, true];
        cachedTokenResult = result;
        reauthRequested = false;
        return result;
      } else if (refreshTransientError) {
        console.warn('[AuthRefresh] getToken refresh transient failure, skipping redirect');
        shouldCacheTokenResult = false;
        return [null, false];
      } else {
        console.warn('[AuthRefresh] getToken refresh returned null (token present in storage)'); 
      }
    } catch (error) {
      console.warn('[AuthRefresh] getToken refresh attempt failed', {
        error: error instanceof Error ? error.message : String(error)
      });
    }

    const publicToken = getFromLocalStorageWithExpiry('public_access_token');
    if (publicToken) {
      console.info(`${AUTH_TRACE} getToken using stored public token`, {
        token: maskToken(publicToken),
      });
      const result: [string | null, boolean] = [publicToken, false];
      cachedTokenResult = result;
      reauthRequested = false;
      return result;
    }

    const access_token = window.location.hash.split('&')[0].split('=')[1];
    if (access_token) {
      console.info(`${AUTH_TRACE} getToken received implicit token`, {
        token: maskToken(access_token),
      });
      setLocalStorageWithExpiry('public_access_token', access_token, 3600);
      window.location.hash = '';
      const result: [string | null, boolean] = [access_token, false];
      cachedTokenResult = result;
      reauthRequested = false;
      return result;
    }

    console.info(`${AUTH_TRACE} getToken none found, returning null`);
    const result: [string | null, boolean] = [null, false];
    cachedTokenResult = result;
    return result;
  })();

  try {
    const resolved = await getTokenInFlight;
    if (shouldCacheTokenResult) {
      cachedTokenResult = resolved;
    }
    return resolved;
  } finally {
    getTokenInFlight = null;
  }
};

export const getRefreshToken = async () => {
  if (refreshInFlight) {
    console.info(`${AUTH_TRACE} getRefreshToken returning in-flight promise`);
    return refreshInFlight;
  }

  console.info('[AuthRefresh] getRefreshToken start');

  // refresh token that has been previously stored
  // Try expiry-aware storage first, then fall back to direct localStorage
  let refreshToken: string | null = null;
  try {
    refreshToken = getFromLocalStorageWithExpiry('refresh_token') as string | null;
  } catch (error) {
    console.warn(`${AUTH_TRACE} getRefreshToken failed to parse refresh_token`, error);
  }
  if (!refreshToken) {
    try {
      refreshToken = getFromLocalStorageWithExpiry('spotify_refresh_token') as string | null;
    } catch (error) {
      console.warn(`${AUTH_TRACE} getRefreshToken failed to parse spotify_refresh_token`, error);
    }
  }
  if (!refreshToken) {
    // Fallback to direct localStorage for backward compatibility
    refreshToken = localStorage.getItem('refresh_token');
  }

  if (!refreshToken) {
    console.warn('[AuthRefresh] getRefreshToken missing refresh token, triggering reauth');
    if (!reauthRequested) {
      reauthRequested = true;
      logInWithSpotify(false);
    }
    return null;
  }

  const url = 'https://accounts.spotify.com/api/token';

  const payload = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      client_id,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    }),
  };
  refreshInFlight = (async () => {
    console.info(`${AUTH_TRACE} getRefreshToken posting to Spotify`, {
      hasRefreshToken: !!refreshToken,
      refreshToken: maskToken(refreshToken)
    });
    let response: Response | null = null;
    let parsed: any = null;
    try {
      response = await fetch(url, payload);
      const responseText = await response.text();
      try {
        parsed = JSON.parse(responseText);
      } catch (parseError) {
        console.warn('[AuthRefresh] getRefreshToken failed to parse JSON response', {
          status: response?.status,
          statusText: response?.statusText,
          responseText: responseText.substring(0, 200), // First 200 chars for debugging
          error: parseError instanceof Error ? parseError.message : String(parseError)
        });
        parsed = null;
      }
    } catch (error) {
      console.warn(`${AUTH_TRACE} getRefreshToken fetch errored`, {
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }

    if (!response?.ok || !parsed?.access_token) {
      console.warn('[AuthRefresh] getRefreshToken refresh failed', {
        refreshToken: maskToken(refreshToken),
        status: response?.status ?? null,
        statusText: response?.statusText ?? null,
        responseOk: response?.ok ?? null,
        hasParsed: !!parsed,
        hasAccessToken: !!parsed?.access_token,
        parsedKeys: parsed ? Object.keys(parsed) : null,
        body: parsed ?? null
      });
      const isInvalidGrant =
        response?.status === 400 ||
        response?.status === 401 ||
        parsed?.error === 'invalid_grant' ||
        parsed?.error === 'invalid_client';
      refreshTransientError = !isInvalidGrant;
      if (isInvalidGrant) {
        if (!reauthRequested) {
          reauthRequested = true;
          logInWithSpotify(false);
        }
      }
      return null;
    }

    try {
      if (typeof parsed.scope === 'string') {
        localStorage.setItem('cmf_last_token_scope', parsed.scope);
      }
    } catch {}

    refreshTransientError = false;
    const hasNewRefreshToken = !!parsed.refresh_token;
    console.info('[AuthRefresh] refresh success', {
      newRefreshTokenReceived: hasNewRefreshToken,
      accessToken: maskToken(parsed.access_token),
      storedRefreshToken: maskToken(hasNewRefreshToken ? parsed.refresh_token : refreshToken),
    });
    const ttlMs = parsed.expires_in * 1000; // expires_in is in seconds, convert to milliseconds
    setLocalStorageWithExpiry('access_token', parsed.access_token, ttlMs);
    setLocalStorageWithExpiry('spotify_access_token', parsed.access_token, ttlMs);
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + parsed.access_token;
    if (parsed.refresh_token) {
      setLocalStorageWithExpiry('refresh_token', parsed.refresh_token, REFRESH_TOKEN_TTL_MS);
      setLocalStorageWithExpiry('spotify_refresh_token', parsed.refresh_token, REFRESH_TOKEN_TTL_MS);
    } else {
      // Re-store existing token to extend TTL
      setLocalStorageWithExpiry('refresh_token', refreshToken, REFRESH_TOKEN_TTL_MS);
      setLocalStorageWithExpiry('spotify_refresh_token', refreshToken, REFRESH_TOKEN_TTL_MS);
    }
    reauthRequested = false;
    return parsed.access_token;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
};

export default { logInWithSpotify, getToken, getRefreshToken };
